import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Box, Button } from '@mui/material';
import { DataGrid, GridPaginationModel, GridSortDirection, GridSortModel } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { CustomSelect } from 'components/elements';
import toastService from 'components/elements/Toastify';
import { ManageCityAliasModal } from 'components/modules/ManageCityAliasModal';
import { UpdateCityAliasAnchor } from 'components/modules/UpdateCityAliasAnchor';
import { UtilityMapProcessingWarning } from 'components/modules/UtilityMapProcessingWarning';
import { ROUTES } from 'routes';
import {
	ICityAliasByProviderItem,
	ICityAliasFormValues,
	IErrorResponse,
	IQuery,
	IUtilityProviderDetails,
	IUtilityProvidersResponse,
} from 'types';
import { PAGINATION_LIMIT } from 'utils/constants';
import { DATE_FORMAT, SORT_DIR } from 'utils/enums';
import { CITY_ALIASES_BY_PROVIDER_FIELDS_NAME } from 'utils/enums/table-fields-name';
import { Rights } from 'utils/enums/rights.enum';
import { formatQueryString, getErrorMessage, parseQueryString } from 'utils/helpers';
import { formatDateView } from 'utils/helpers/date';
import { getTableSetting } from 'utils/helpers/table-setting';
import { useCityAutocomplete, useModal, useUtilityMapStatus } from 'utils/hooks';
import { usePermissions } from 'utils/hooks/use-permissions';
import { CityAliasesByProviderService, ProvidersService } from 'utils/services';
import { COLUMNS_CONFIG, ICityAliasByProviderRow } from './config';
import s from './index.module.scss';

const DEVICE_FP = '6037e65eefbdasdfadsfasdf3';
const OPTIONS_LIMIT = 100;

const INITIAL_VALUES: ICityAliasFormValues = {
	name: '',
	cityId: '',
	providerId: '',
};

const normalizeUtilityProviders = (response: IUtilityProvidersResponse): IUtilityProviderDetails[] =>
	Array.isArray(response) ? response : response.results || [];

const loadAllUtilityProviders = async (): Promise<IUtilityProviderDetails[]> => {
	const results: IUtilityProviderDetails[] = [];
	let offset = 0;

	while (true) {
		const response = await ProvidersService.getUtilityProviders({
			offset,
			limit: OPTIONS_LIMIT,
			deviceFP: DEVICE_FP,
		});
		const batch = normalizeUtilityProviders(response.data);

		results.push(...batch);

		if (batch.length < OPTIONS_LIMIT) {
			break;
		}

		offset += OPTIONS_LIMIT;
	}

	return results;
};

export const CityAliasesByProviderPage: FC = () => {
	const { search } = useLocation();
	const navigate = useNavigate();
	const checkRights = usePermissions();
	const [columnWidths, setColumnWidths] = useState({});
	const [isCreateModalOpen, handleOpenCreateModal, handleCloseCreateModal] = useModal();
	const [providerFilter, setProviderFilter] = useState('');
	const { status: utilityMapStatus } = useUtilityMapStatus('byCity');
	const [state, setState] = useState<{
		results: ICityAliasByProviderItem[];
		total: number;
		isLoading: boolean;
		providerOptions: { title: string; value: string }[];
	}>({
		results: [],
		total: 0,
		isLoading: false,
		providerOptions: [],
	});
	const {
		cityOptions,
		handleLoadMoreCities,
		handleSearchCities,
		hasMoreCityOptions,
		isCityOptionsLoading,
		resetCityAutocomplete,
	} = useCityAutocomplete({ deviceFP: DEVICE_FP });

	const query = useMemo(() => parseQueryString<IQuery>(search, { parseBooleans: true, parseNumbers: true }), [search]);

	const requestBody = useMemo(
		() => ({
			offset: (query.page || 0) * (query.pageSize || PAGINATION_LIMIT.SMALL),
			limit: query.pageSize || PAGINATION_LIMIT.SMALL,
			orderBy: [
				{
					field: query.field || CITY_ALIASES_BY_PROVIDER_FIELDS_NAME.NAME,
					direction: query.sort || SORT_DIR.ASC,
				},
			],
			searchBy: {
				name: query.searchQuery,
				providerName: providerFilter || undefined,
			},
			deviceFP: DEVICE_FP,
		}),
		[query, providerFilter]
	);

	const loadAliases = useCallback(async (): Promise<void> => {
		setState((prevState) => ({ ...prevState, isLoading: true }));

		try {
			const response = await CityAliasesByProviderService.getCityAliasesByProvider(requestBody);
			setState((prevState) => ({
				...prevState,
				results: response.data.results || [],
				total: response.data.total || 0,
				isLoading: false,
			}));
		} catch (error) {
			setState((prevState) => ({ ...prevState, isLoading: false }));
			toastService.error(getErrorMessage(error as AxiosError<IErrorResponse>));
		}
	}, [requestBody]);

	useEffect(() => {
		void loadAliases();
	}, [loadAliases]);

	useEffect(() => {
		const loadProviderOptions = async () => {
			try {
				const providers = await loadAllUtilityProviders();
				setState((prevState) => ({
					...prevState,
					providerOptions: providers.map(({ id, name }) => ({
						title: name,
						value: `${id}`,
					})),
				}));
			} catch (error) {
				toastService.error(getErrorMessage(error as AxiosError<IErrorResponse>));
			}
		};

		void loadProviderOptions();
	}, []);

	useEffect(() => {
		setColumnWidths(getTableSetting(ROUTES.cityAliasesByProvider));
	}, [requestBody]);

	COLUMNS_CONFIG.forEach((item: GridColDef) => {
		// @ts-ignore
		if (columnWidths && columnWidths[item.field]) {
			// @ts-ignore
			item.width = columnWidths[item.field];
			item.flex = undefined;
		}
	});

	const handleColumnResize = (params: any) => {
		const newWidths = {
			...columnWidths,
			[params.colDef.field]: params.width,
		};
		setColumnWidths(newWidths);
		localStorage.setItem(ROUTES.cityAliasesByProvider, JSON.stringify(newWidths));
	};

	const handleChangePage = ({ page, pageSize }: GridPaginationModel): void => {
		navigate(
			{
				search: formatQueryString({
					...query,
					page,
					pageSize,
				}),
			},
			{ replace: true }
		);
	};

	const handleSortField = (model: GridSortModel): void => {
		const { field, sort } = model[0] || {};
		navigate(
			{
				search: formatQueryString({
					...query,
					field,
					sort,
					page: 0,
				}),
			},
			{ replace: true }
		);
	};

	const handleCreate = async (values: ICityAliasFormValues) => {
		try {
			await CityAliasesByProviderService.createCityAliasByProvider({
				name: values.name.trim(),
				cityId: Number(values.cityId),
				providerId: Number(values.providerId),
				deviceFP: DEVICE_FP,
			});
			toastService.success('Аліас міста успішно створено');
			handleCloseCreateModal();
			await loadAliases();
		} catch (error) {
			toastService.error(getErrorMessage(error as AxiosError<IErrorResponse>));
		}
	};

	const handleOpenCreate = () => {
		resetCityAutocomplete();
		handleOpenCreateModal();
	};

	const rows = useMemo<ICityAliasByProviderRow[]>(
		() =>
			state.results.map((item) => ({
				...item,
				'city.name': item.city?.name || 'Не визначено',
				'provider.name': item.provider?.name || '',
				createdAt: formatDateView(item.createdAt, DATE_FORMAT.DATE_FULL),
				updatedAt: formatDateView(item.updatedAt, DATE_FORMAT.DATE_FULL),
			})),
		[state.results]
	);

	const columns = useMemo<GridColDef<ICityAliasByProviderRow>[]>(
		() =>
			checkRights(Rights.STREETS_MANAGE)
				? [
						...COLUMNS_CONFIG,
						{
							field: 'edit',
							headerName: ' ',
							sortable: false,
							align: 'center',
							width: 60,
							renderCell: (params) => (
								<UpdateCityAliasAnchor
									row={params.row}
									cityOptions={cityOptions}
									cityOptionsLoading={isCityOptionsLoading}
									onCitySearch={handleSearchCities}
									onCityLoadMore={handleLoadMoreCities}
									hasMoreCityOptions={hasMoreCityOptions}
									providerOptions={state.providerOptions}
									onSuccess={loadAliases}
								/>
							),
						},
				  ]
				: COLUMNS_CONFIG,
		[
			checkRights,
			cityOptions,
			isCityOptionsLoading,
			handleSearchCities,
			handleLoadMoreCities,
			hasMoreCityOptions,
			state.providerOptions,
			loadAliases,
		]
	);

	const providerFilterOptions = useMemo(
		() => [{ value: '', title: 'Показати будь-який' }, ...state.providerOptions.map((item) => ({ value: item.title, title: item.title }))],
		[state.providerOptions]
	);

	return (
		<>
			<UtilityMapProcessingWarning status={utilityMapStatus} />
			<Box className={s.toolbar}>
				<Box className={s.filter}>
					<Box>
						<CustomSelect
							value={providerFilter}
							name="Провайдер"
							onChange={(event: any) => setProviderFilter(event.target.value)}
							placeholder="Провайдер"
							options={providerFilterOptions}
						/>
					</Box>
					<Button variant="contained" onClick={() => setProviderFilter('')}>
						Очистити
					</Button>
				</Box>
				{checkRights(Rights.STREETS_MANAGE) && (
					<Button variant="contained" onClick={handleOpenCreate}>
						Додати
					</Button>
				)}
			</Box>

			<Box className={s.tableWrapper}>
				<DataGrid
					onColumnResize={handleColumnResize}
					disableColumnFilter
					disableRowSelectionOnClick
					rows={rows}
					columns={columns}
					rowCount={state.total}
					loading={state.isLoading}
					getRowId={(row) => row.id}
					initialState={{
						pagination: {
							paginationModel: { page: query.page || 0, pageSize: query.pageSize || PAGINATION_LIMIT.SMALL },
						},
						sorting: {
							sortModel: [
								{
									field: query.field || CITY_ALIASES_BY_PROVIDER_FIELDS_NAME.NAME,
									sort: query.sort as GridSortDirection,
								},
							],
						},
					}}
					slotProps={{
						pagination: {
							labelRowsPerPage: 'Показувати :',
							page: query.page || 0,
							rowsPerPage: query.pageSize || PAGINATION_LIMIT.SMALL,
						},
					}}
					paginationMode="server"
					sortingMode="server"
					onSortModelChange={handleSortField}
					onPaginationModelChange={handleChangePage}
					className={s.table}
					pageSizeOptions={[PAGINATION_LIMIT.SMALL, PAGINATION_LIMIT.MIDDLE, PAGINATION_LIMIT.BIG]}
				/>
			</Box>

			<ManageCityAliasModal
				title="Створити аліас міста"
				isOpen={isCreateModalOpen}
				onClose={handleCloseCreateModal}
				initialValues={INITIAL_VALUES}
				onSubmit={handleCreate}
				buttonText="Додати"
				cityOptions={cityOptions}
				cityOptionsLoading={isCityOptionsLoading}
				onCitySearch={handleSearchCities}
				onCityLoadMore={handleLoadMoreCities}
				hasMoreCityOptions={hasMoreCityOptions}
				providerOptions={state.providerOptions}
			/>
		</>
	);
};
